import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type StreamData, type LiveBlock } from "./api";
import { useDashboardStore, type StreamStats } from "./store";

function fmtT(ms: number): string {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
}

/** sqlite datetime('now') strings are UTC without a zone marker */
function age(createdAt: string): string {
    const t = new Date(createdAt.replace(" ", "T") + "Z").getTime();
    const s = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (s < 90) return `${s}s ago`;
    if (s < 5400) return `${Math.round(s / 60)}m ago`;
    return `${(s / 3600).toFixed(1)}h ago`;
}

function AnnouncementRow({ b }: { b: LiveBlock }) {
    return (
        <div style={{ padding: "6px 0", borderBottom: "1px solid #1d1d1d", lineHeight: 1.5 }}>
            <div style={{ fontSize: "0.75em", color: "#666", fontVariantNumeric: "tabular-nums" }}>
                {fmtT(b.t0_ms)} – {fmtT(b.t1_ms)}
                <span style={{ marginLeft: 8, color: "#444" }}>{age(b.created_at)}</span>
            </div>
            {b.status === "done"
                ? <div style={{ color: "#e0e0e0" }}>{b.text || <i style={{ color: "#555" }}>(no speech found)</i>}</div>
                : <div style={{ color: b.status === "error" ? "#aa3333" : "#aa8833" }}>
                    {b.status === "error" ? "transcription failed" : "transcribing…"}
                  </div>}
        </div>
    );
}

function stTotal(s: StreamStats | undefined): number {
    if (!s) return 0;
    return s.pending + s.downloading + s.stored + s.failed + s.expired;
}

function BallroomCard({ stream: s, onOpen }: { stream: StreamData; onOpen: () => void }) {
    const [expanded, setExpanded] = useState(false);
    const { data } = useQuery({
        queryKey: ["liveBlocks", s.stream_key],
        queryFn: () => api.liveBlocks(s.stream_key),
        refetchInterval: 15_000,   // SSE live:block/live:caption invalidates sooner
        staleTime: 5_000,
    });
    const vs = useDashboardStore(st => st.stats[s.stream_key]);
    const durMin = ((vs?.totalDuration || 0) / 60).toFixed(0);

    const gate = data?.gate ?? null;
    const blocks = data?.blocks ?? [];
    const shown = expanded ? blocks : blocks.slice(0, 3);

    return (
        <div className="panel" style={{ padding: 14, minWidth: 320, flex: "1 1 380px", maxWidth: 560 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <span style={{ fontWeight: 600, cursor: "pointer" }} onClick={onOpen} title="open stream">
                    <span className="live-dot" /> {s.title || s.stream_key}
                </span>
                <span style={{ fontSize: "0.75em", color: "#666", fontVariantNumeric: "tabular-nums" }}>
                    {durMin}min · {(vs?.stored ?? 0).toLocaleString()}/{stTotal(vs).toLocaleString()}
                </span>
            </div>

            <div style={{ marginBottom: 8 }}>
                {gate ? (
                    <span style={{
                        fontSize: "0.78em", padding: "2px 8px", borderRadius: 10,
                        background: gate.state === "music" ? "#173d22" : gate.state === "speech" ? "#3d3117" : "#222",
                        color: gate.state === "music" ? "#33aa55" : gate.state === "speech" ? "#d0a030" : "#777",
                    }}>
                        {gate.state === "music" ? "♪ dancing" : gate.state === "speech" ? "🎤 announcing" : "· quiet"} since {fmtT(gate.sinceMs)}
                    </span>
                ) : (
                    <span style={{ fontSize: "0.78em", padding: "2px 8px", borderRadius: 10, background: "#222", color: "#666" }}>
                        gate idle
                    </span>
                )}
            </div>

            {blocks.length === 0
                ? <div style={{ color: "#555", fontSize: "0.85em" }}>no announcements detected yet</div>
                : <>
                    {shown.map(b => <AnnouncementRow key={b.id} b={b} />)}
                    {blocks.length > 3 && (
                        <div style={{ marginTop: 6, color: "#da4165", fontSize: "0.8em", cursor: "pointer", userSelect: "none" }}
                            onClick={() => setExpanded(e => !e)}>
                            {expanded ? "show less ▲" : `show ${blocks.length - 3} more ▼`}
                        </div>
                    )}
                  </>}
        </div>
    );
}

/** Live event overview: one card per active ballroom, newest announcements first. */
export function LiveDashboard({ streams, onSelectStream }: {
    streams: StreamData[];
    onSelectStream: (key: string) => void;
}) {
    const live = streams.filter(s => s.status === "active" && s.type !== "dvr");

    return (
        <div>
            <h2 style={{ marginTop: 0 }}>
                live now
                <span style={{ color: "#666", fontSize: "0.6em", marginLeft: 10 }}>
                    {live.length ? `${live.length} ballroom${live.length > 1 ? "s" : ""}` : ""}
                </span>
            </h2>
            {live.length === 0
                ? <div className="empty">no live streams right now</div>
                : <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                    {live.map(s => (
                        <BallroomCard key={s.stream_key} stream={s} onOpen={() => onSelectStream(s.stream_key)} />
                    ))}
                  </div>}
        </div>
    );
}
